import React, { useEffect } from 'react';
import { Editor } from '@tiptap/react';
import ToolbarButton from '../ToolbarButton';
import HeadingSelector from '../HeadingSelector/HeadingSelector';
import styles from './Toolbar.module.css';

export default function Toolbar({ editor }: { editor: Editor | null }) {
  const [heading, setHeading] = React.useState(0);

  const setLink = React.useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .unsetLink()
        .run();

      return;
    }

    // update link
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url })
      .run();
  }, [editor]);

  useEffect(() => {
    if (editor) {
      const handler = () => {
        setHeading(editor.getAttributes('heading').level || 0);
      };

      editor.on('update', handler);

      return () => editor.off('update', handler);
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={styles.toolbarContainer}>
      <HeadingSelector
        value={heading}
        onChange={value => {
          if (value === 0) {
            editor
              .chain()
              .focus()
              .setParagraph()
              .run();
          } else {
            editor
              .chain()
              .focus()
              .toggleHeading({ level: value || undefined })
              .run();
          }
          setHeading(value);
        }}
      />

      <ToolbarButton
        active={editor.isActive('bold')}
        disabled={
          !editor
            .can()
            .chain()
            .focus()
            .toggleBold()
            .run()
        }
        title="Bold"
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleBold()
            .run()
        }
      >
        <i className="material-icons">format_bold</i>
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('italic')}
        disabled={
          !editor
            .can()
            .chain()
            .focus()
            .toggleItalic()
            .run()
        }
        title="Italic"
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleItalic()
            .run()
        }
      >
        <i className="material-icons">format_italic</i>
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('underline')}
        disabled={
          !editor
            .can()
            .chain()
            .focus()
            .toggleUnderline()
            .run()
        }
        title="Underline"
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleUnderline()
            .run()
        }
      >
        <i className="material-icons">format_underlined</i>
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('strike')}
        disabled={
          !editor
            .can()
            .chain()
            .focus()
            .toggleStrike()
            .run()
        }
        title="Strike"
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleStrike()
            .run()
        }
      >
        <i className="material-icons">format_strikethrough</i>
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive({ textAlign: 'left' })}
        title="Left"
        onClick={() =>
          editor
            .chain()
            .focus()
            .setTextAlign('left')
            .run()
        }
      >
        <i className="material-icons" style={{ fontSize: 20 }}>
          format_align_left
        </i>
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive({ textAlign: 'center' })}
        title="Center"
        onClick={() =>
          editor
            .chain()
            .focus()
            .setTextAlign('center')
            .run()
        }
      >
        <i className="material-icons" style={{ fontSize: 20 }}>
          format_align_center
        </i>
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive({ textAlign: 'right' })}
        title="Right"
        onClick={() =>
          editor
            .chain()
            .focus()
            .setTextAlign('right')
            .run()
        }
      >
        <i className="material-icons" style={{ fontSize: 20 }}>
          format_align_right
        </i>
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive({ textAlign: 'justify' })}
        title="Justify"
        onClick={() =>
          editor
            .chain()
            .focus()
            .setTextAlign('justify')
            .run()
        }
      >
        <i className="material-icons" style={{ fontSize: 20 }}>
          format_align_justify
        </i>
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('bulletList')}
        disabled={
          !editor
            .can()
            .chain()
            .focus()
            .toggleBulletList()
            .run()
        }
        title="Bullet List"
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleBulletList()
            .run()
        }
      >
        <i className="material-icons" style={{ fontSize: 20 }}>
          format_list_bulleted
        </i>
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('orderedList')}
        disabled={
          !editor
            .can()
            .chain()
            .focus()
            .toggleOrderedList()
            .run()
        }
        title="Ordered List"
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleOrderedList()
            .run()
        }
      >
        <i className="material-icons" style={{ fontSize: 20 }}>
          format_list_numbered
        </i>
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('link')}
        title="Link"
        onClick={() => setLink()}
      >
        <i className="material-icons">link</i>
      </ToolbarButton>
    </div>
  );
}
